import { SWAP_REASONS } from '@/routes/measurement-group/field-report/types';
import { Path } from 'react-hook-form';
import { z } from 'zod';

const itemSchema = z.object({
	value: z.string(),
	label: z.string()
});

const fieldReportSchema = z
	.object({
		poId: z.string().optional().default(''),
		performedAt: z.date({ message: 'Date is required' }).optional().nullable().default(null),
		deploymentTypeId: z.string().optional().nullable().default(null),
		notes: z
			.string()
			.optional()
			.transform(s => s ?? null),
		hasExternalAntennas: z.boolean().optional().default(false),
		swapReason: z.nativeEnum(SWAP_REASONS).nullable().default(null)
	})
	.superRefine((data, ctx) => {
		const anyFieldFilled =
			(data.poId && data.poId.trim() !== '') ||
			data.performedAt !== null ||
			(data.deploymentTypeId && data.deploymentTypeId.trim() !== '') ||
			(data.notes && data.notes.trim() !== '') ||
			data.hasExternalAntennas === true ||
			data.swapReason !== null;

		if (anyFieldFilled) {
			if (!data.poId || data.poId.trim() === '') {
				ctx.addIssue({
					path: ['poId'],
					code: z.ZodIssueCode.custom,
					message: 'PO ID is required.'
				});
			}

			if (!data.performedAt) {
				ctx.addIssue({
					path: ['performedAt'],
					code: z.ZodIssueCode.custom,
					message: 'Date is required.'
				});
			}

			if (!data.deploymentTypeId || data.deploymentTypeId.trim() === '') {
				ctx.addIssue({
					path: ['deploymentTypeId'],
					code: z.ZodIssueCode.custom,
					message: 'Deployment Type is required.'
				});
			}

			if (data.swapReason === null) {
				ctx.addIssue({
					path: ['swapReason'],
					code: z.ZodIssueCode.custom,
					message: 'Swap Reason is required.'
				});
			}
		}
	});

export const poFileUploadSchema = z
	.object({
		processObjectId: z.string().optional().nullable().default(null),
		title: z.string({ message: 'Title is required.' }).optional().default(''),
		description: z.string({ message: 'Description is required.' }).optional().default(''),
		file: z.instanceof(File, { message: 'A valid file must be uploaded.' }).optional()
	})
	.superRefine((data, ctx) => {
		const anyFieldFilled =
			(data.processObjectId && data.processObjectId.trim() !== '') ||
			(data.title && data.title.trim() !== '') ||
			(data.description && data.description.trim() !== '') ||
			data.file;

		if (anyFieldFilled) {
			if (!data.processObjectId || data.processObjectId.trim() === '') {
				ctx.addIssue({
					path: ['processObjectId'],
					code: z.ZodIssueCode.custom,
					message: 'Process Object ID is required.'
				});
			}
			if (!data.title || data.title.trim() === '') {
				ctx.addIssue({
					path: ['title'],
					code: z.ZodIssueCode.custom,
					message: 'Title is required.'
				});
			}
			if (!data.description || data.description.trim() === '') {
				ctx.addIssue({
					path: ['description'],
					code: z.ZodIssueCode.custom,
					message: 'Description is required.'
				});
			}
			if (!data.file) {
				ctx.addIssue({
					path: ['file'],
					code: z.ZodIssueCode.custom,
					message: 'File is required.'
				});
			}
		}
	});

export const swapDeviceSchema = z.object({
	newDeviceId: z.array(itemSchema).nonempty('Device ID is required'),
	antennaTypeId: z.array(itemSchema).nonempty('Antenna type is required'),
	pvPanelTypeId: z.array(itemSchema).nonempty('PV panel type is required'),
	upload: poFileUploadSchema,
	fieldReport: fieldReportSchema
});

export type SwapDeviceFormSchemaType = z.infer<typeof swapDeviceSchema>;

export const stepFields: Record<number, Path<SwapDeviceFormSchemaType>[]> = {
	0: ['newDeviceId', 'antennaTypeId', 'pvPanelTypeId']
};
